import { Flex } from 'antd';
import ExpenseCard from './expenseCard';
import { Spin } from '../../Ant';
import { useGetMatterOrInvoiceExpenses } from '../../../hooks/useGetMatterOrInvoiceExpenses';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { EntryEntity } from '../../../constants/entries';
import { useMemo, useState } from 'react';
import { groupExpensesByDay } from '../../../constants/expense';
import { ExpenseFilters, ExpenseStatus } from '../../../interface/expense';
import { useUser } from '../../../context/UserContext';
import { IFilterOption } from '../../../interface/transaction';
import NoData from '../../dashboard/noData';
import { MoneySendEmpty } from '../../icons';
import dayjs from 'dayjs';
import { useQueryClient } from '@tanstack/react-query';
import SearchInputWithFilters from '../../dashboard/SearchInputWithFilters';
import { IMatter } from '../../../interface';
import SkeletonCardList from '../../common/skeleton/skeletonCardList';
import CollapsibleListLayout from '../../collapsibleListLayout/collapsibleListLayout';
import CollapsibleDateGroup from '../../collapsibleDateGroup/collapsibleDateGroup';
import {
  DateGroupList,
  toDateGroupKey,
  useCollapsedDateGroups,
} from '../../../hooks/useCollapsedDateGroups';
import { useFetchAllPages } from '../../../hooks/useFetchAllPages';

const ExpenseListing = ({
  entity,
  matter,
  isBillableMatter,
}: {
  entity: string;
  matter?: IMatter;
  isBillableMatter?: boolean;
}) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { user } = useUser();
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedViewFilter, setSelectedViewFilter] = useState<IFilterOption>();
  const multipleFilters: IFilterOption[] = useMemo(() => {
    return [
      {
        label: t('expense:filter.myOwn'),
        value: ExpenseFilters.MY,
      },
      {
        label: t('expense:filter.otherAuthors'),
        value: ExpenseFilters.OTHER,
      },
    ];
  }, [t]);
  const [selectedAuthorFilter, setSelectedAuthorFilter] = useState<IFilterOption[] | undefined>(
    matter?.showOnlyOwnEntries
      ? multipleFilters.filter((opt) => opt.value === ExpenseFilters.MY)
      : undefined,
  );
  const handleAuthorFilter = () => {
    if (!selectedAuthorFilter) {
      return undefined;
    }

    if (
      selectedAuthorFilter.some(
        (val) =>
          val.value.includes(ExpenseFilters.MY) &&
          selectedAuthorFilter.some((value) => value.value.includes(ExpenseFilters.OTHER)),
      )
    ) {
      return undefined;
    } else if (selectedAuthorFilter.some((val) => val.value.includes(ExpenseFilters.MY))) {
      return user?.id;
    } else if (selectedAuthorFilter.some((val) => val.value.includes(ExpenseFilters.OTHER))) {
      return `$not:${user?.id}`;
    }
    return undefined;
  };

  const handleStatusFilter = () => {
    if (entity === EntryEntity.MATTER) {
      if (selectedViewFilter?.value.includes(ExpenseFilters.OPEN_EXPENSES)) {
        return ExpenseStatus.OPEN;
      }

      if (!isBillableMatter) {
        return undefined;
      }

      return `$in:${ExpenseStatus.OPEN},${ExpenseStatus.REVIEWED},${ExpenseStatus.PRELIM_INVOICE}`;
    }
    return undefined;
  };
  const statusFilter = handleStatusFilter();
  const {
    refetch,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    data: paginatedExpensesData,
    isLoading: isExpensesLoading,
  } = useGetMatterOrInvoiceExpenses({
    id,
    type: entity,
    limit: 50,
    sortBy: ['expenseDate:DESC'],
    relations: ['expenseUser:fullName', 'unit'],

    search: searchValue,
    extraFilters: {
      expenseUserId: handleAuthorFilter(),
      ...(entity === EntryEntity.INVOICE ? { preliminaryInvoiceId: `${id}` } : {}),
      ...(entity === EntryEntity.MATTER && statusFilter ? { status: statusFilter } : {}),
    },
    fields: [
      'subject',
      'expenseDate',
      'salesPriceNet',
      'status',
      'unitPriceNet',
      'units',
      'hasImportantNotes',
      'hasNotes',
    ],
  });

  const uniqueFilters: IFilterOption[] = useMemo(() => {
    return [
      {
        label: t('expense:filter.allExpenses'),
        value: ExpenseFilters.ALL_EXPENSES,
        filterVisibleWhenSelected: false,
      },
      {
        label: t('expense:filter.openExpenses'),
        value: ExpenseFilters.OPEN_EXPENSES,
      },
    ];
  }, [t]);
  const groupedExpenses = useMemo(
    () => groupExpensesByDay(paginatedExpensesData),
    [paginatedExpensesData],
  );
  const { isCollapsed, toggle, getToggleAllControl } = useCollapsedDateGroups(
    `${DateGroupList.matterExpenses}:${id}`,
  );
  useFetchAllPages({ hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage });
  const renderLoader = () => {
    if (isFetching) {
      return <Spin size="small" />;
    }
    return null;
  };
  return (
    <CollapsibleListLayout
      testId="expense-listing"
      header={
        <SearchInputWithFilters
          dateGroupToggle={getToggleAllControl(
            groupedExpenses.map((group) => toDateGroupKey(group?.date)),
          )}
          searchTitle={t('expense:search')}
          filterTitle={t('expense:filter.label')}
          checkboxOptions={multipleFilters}
          radioOptions={uniqueFilters}
          handleRefresh={() => {
            queryClient.removeQueries({ queryKey: ['matter-invoice-expenses'] });
            return refetch();
          }}
          radioTitle={t('common:view')}
          checkboxTitle={t('common:author')}
          selectedFilter={Array.from(
            new Map(
              [
                ...(selectedViewFilter ? [selectedViewFilter] : []),
                ...(selectedAuthorFilter ?? []),
              ].map((item) => [item.value, item]),
            ).values(),
          )}
          setSelectedFilter={(val) => setSelectedViewFilter(val)}
          setSelectedCheckboxFilter={(val: IFilterOption[]) => {
            if (matter?.showOnlyOwnEntries) {
              const myFilter = multipleFilters.find((f) => f.value === ExpenseFilters.MY);
              setSelectedAuthorFilter(myFilter ? [myFilter] : val);
            } else {
              setSelectedAuthorFilter(val);
            }
          }}
          clearFilter={(val?: string) => {
            if (!val) {
              setSelectedViewFilter(undefined);
              if (matter?.showOnlyOwnEntries) {
                const myFilter = multipleFilters.find((f) => f.value === ExpenseFilters.MY);
                setSelectedAuthorFilter(myFilter ? [myFilter] : undefined);
              } else {
                setSelectedAuthorFilter(undefined);
              }
              return;
            }
            if (val) {
              setSelectedAuthorFilter((prev) => (prev ?? []).filter((item) => item.value !== val));
            }
            if (selectedViewFilter?.value === val) {
              setSelectedViewFilter(undefined);
            }
          }}
          handleSearch={(val: string) => {
            setSearchValue(val);
          }}
          disabledCheckboxValues={
            matter?.showOnlyOwnEntries ? [ExpenseFilters.OTHER, ExpenseFilters.MY] : []
          }
          filtersDisabledInfoMessage={
            matter?.showOnlyOwnEntries
              ? t('transaction:showOnlyOwnEntriesFilterLockedMessage')
              : undefined
          }
        />
      }
    >
      <Flex vertical className="h-full gap-gutter">
        {groupedExpenses && Object.entries(groupedExpenses).length > 0 && (
          <>
            {groupedExpenses.map((data) => {
              const groupKey = toDateGroupKey(data?.date);
              return (
                <CollapsibleDateGroup
                  key={groupKey}
                  title={data?.date ? dayjs(data?.date).format('MMM DD, YYYY') : ''}
                  itemCount={t('expense:expenseCount', { count: data?.expenses.length })}
                  collapsed={isCollapsed(groupKey)}
                  onToggle={() => toggle(groupKey)}
                >
                  {data?.expenses.map((expense) => (
                    <ExpenseCard data-testid="expense-card" key={expense?.id} expense={expense} />
                  ))}
                </CollapsibleDateGroup>
              );
            })}
          </>
        )}
        {(!paginatedExpensesData ||
          (Object.entries(paginatedExpensesData).length === 0 && !isFetching)) && (
          <div className="flex-grow d-flex justify-center items-center">
            <NoData
              header={t('expense:noExpenses')}
              subHeader={t('common:tryChangingSearchCriteria')}
              icon={<MoneySendEmpty />}
            />
          </div>
        )}
        {isExpensesLoading ? (
          <SkeletonCardList rows={[1, 1, 2]} withSubcontentOnFirstRow />
        ) : (
          renderLoader()
        )}
      </Flex>
    </CollapsibleListLayout>
  );
};

export default ExpenseListing;
